import { useState } from "react";
import { Plus, Trash2, CreditCard, AlertTriangle } from "lucide-react";
import { fmt, genId } from "../../context/AppContext";

const DEFAULT_SUBS = [
  { id: "s1", name: "Netflix", amount: 18, category: "Entertainment", renewalDay: 10, active: true },
  { id: "s2", name: "Spotify", amount: 11, category: "Entertainment", renewalDay: 10, active: true },
  { id: "s3", name: "Amazon Prime", amount: 9, category: "Shopping", renewalDay: 12, active: true },
  { id: "s4", name: "Gym", amount: 45, category: "Health", renewalDay: 1, active: true },
  { id: "s5", name: "iCloud+", amount: 3, category: "Cloud", renewalDay: 5, active: true },
  { id: "s6", name: "Disney+", amount: 14, category: "Entertainment", renewalDay: 18, active: false },
];

const CATEGORY_COLORS = {
  Entertainment: "text-violet-400 bg-violet-500/10",
  Shopping: "text-sky-400 bg-sky-500/10",
  Health: "text-emerald-400 bg-emerald-500/10",
  Cloud: "text-blue-400 bg-blue-500/10",
  Other: "text-slate-400 bg-slate-500/10",
};

export default function SubscriptionAudit() {
  const [subs, setSubs] = useState(DEFAULT_SUBS);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("Entertainment");
  const [showAdd, setShowAdd] = useState(false);

  const activeSubs = subs.filter((s) => s.active);
  const totalMonthly = activeSubs.reduce((s, sub) => s + sub.amount, 0);
  const totalAnnual = totalMonthly * 12;

  const addSub = () => {
    if (!name || !amount) return;
    setSubs((prev) => [...prev, { id: genId(), name, amount: parseFloat(amount) || 0, category, renewalDay: 1, active: true }]);
    setName(""); setAmount(""); setShowAdd(false);
  };

  const toggle = (id) => setSubs((prev) => prev.map((s) => s.id === id ? { ...s, active: !s.active } : s));
  const remove = (id) => setSubs((prev) => prev.filter((s) => s.id !== id));

  const unnecessaryCost = subs.filter((s) => !s.active).reduce((sum, s) => sum + s.amount, 0);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-slate-900/40 border border-white/5 rounded-xl p-5">
          <p className="text-slate-500 text-xs mb-1">Active Subscriptions</p>
          <p data-testid="active-subs-count" className="font-outfit font-bold text-3xl text-white">{activeSubs.length}</p>
        </div>
        <div className="bg-slate-900/40 border border-white/5 rounded-xl p-5">
          <p className="text-slate-500 text-xs mb-1">Monthly Cost</p>
          <p data-testid="monthly-sub-cost" className="font-outfit font-bold text-3xl text-rose-400 tabular-nums">{fmt(totalMonthly)}</p>
        </div>
        <div className="bg-slate-900/40 border border-white/5 rounded-xl p-5 col-span-2 sm:col-span-1">
          <p className="text-slate-500 text-xs mb-1">Annual Cost</p>
          <p data-testid="annual-sub-cost" className="font-outfit font-bold text-3xl text-rose-300 tabular-nums">{fmt(totalAnnual)}</p>
        </div>
      </div>

      {unnecessaryCost > 0 && (
        <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-slate-300 text-sm">
            You have <span className="text-amber-400 font-semibold">{fmt(unnecessaryCost)}/month</span> ({fmt(unnecessaryCost * 12)}/year) in inactive subscriptions. Cancel or remove them.
          </p>
        </div>
      )}

      {/* Subscription Table */}
      <div className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-slate-400" />
            <h2 className="font-outfit font-semibold text-white">All Subscriptions</h2>
          </div>
          <button onClick={() => setShowAdd(!showAdd)} data-testid="toggle-add-sub" className="flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 font-medium transition-colors">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>

        {showAdd && (
          <div className="flex flex-wrap gap-2 px-5 py-4 bg-slate-900/60 border-b border-white/5">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="flex-1 min-w-[120px] bg-slate-950 border border-slate-800 focus:border-emerald-500/50 rounded-lg px-3 py-1.5 text-sm text-white placeholder-slate-600 outline-none transition-colors" />
            <div className="relative w-24">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">£</span>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-5 pr-2 py-1.5 text-sm text-white outline-none" />
            </div>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-1.5 text-sm text-white outline-none">
              {Object.keys(CATEGORY_COLORS).map((c) => <option key={c}>{c}</option>)}
            </select>
            <button onClick={addSub} data-testid="add-sub-btn" className="px-4 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-xs rounded-lg transition-colors">Add</button>
          </div>
        )}

        <div className="divide-y divide-white/5">
          {subs.map((sub) => (
            <div key={sub.id} data-testid={`sub-row-${sub.id}`} className={`flex items-center gap-3 px-5 py-3.5 hover:bg-white/[0.02] transition-colors ${!sub.active ? "opacity-50" : ""}`}>
              <button onClick={() => toggle(sub.id)} className={`w-4 h-4 rounded-full border-2 flex-shrink-0 transition-colors ${sub.active ? "bg-emerald-500 border-emerald-500" : "border-slate-600"}`} />
              <span className="flex-1 text-slate-200 text-sm">{sub.name}</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${CATEGORY_COLORS[sub.category] || CATEGORY_COLORS.Other}`}>{sub.category}</span>
              <span className="text-xs text-slate-500">Day {sub.renewalDay}</span>
              <span className="font-outfit font-semibold text-sm tabular-nums text-rose-400 w-14 text-right">{fmt(sub.amount)}</span>
              <button onClick={() => remove(sub.id)} className="text-slate-600 hover:text-rose-400 transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
